import {
  assignTableAPIs,
  functionalUpdate,
  type ColumnFiltersState,
  type RowSelectionState,
  type TableFeature,
  type Updater,
} from "@tanstack/react-table";

type SelectionMode = "all" | "page" | "partial" | "none";

export interface SelectAllOptions {
  enableSelectAllMatching?: boolean;
  onIsAllMatchingSelectedChange?: (value: boolean) => void;
}

export interface SelectAllState {
  isAllMatchingSelected: boolean;
}

export interface SelectAllInstance {
  getIsAllMatchingSelected: () => boolean;
  setAllMatchingSelected: (value: boolean) => void;
  getSelectionMode: () => SelectionMode;
}

/** Structural: the concrete `Table<AppTableFeatures, …>` would be a circular import. */
interface WrappedTableApis extends SelectAllInstance {
  atoms: {
    isAllMatchingSelected: { get: () => boolean };
    rowSelection: { get: () => RowSelectionState };
  };
  getIsAllRowsSelected: () => boolean;
  getIsSomeRowsSelected: () => boolean;
  setColumnFilters: (updater: Updater<ColumnFiltersState>) => void;
  setGlobalFilter: (updater: Updater<unknown>) => void;
  setRowSelection: (updater: Updater<RowSelectionState>) => void;
}

const apis = (table: object): WrappedTableApis => table as unknown as WrappedTableApis;

export const selectAllFeature: TableFeature = {
  getDefaultTableOptions: (): SelectAllOptions => ({
    enableSelectAllMatching: false,
  }),

  getInitialState: (state) => ({
    isAllMatchingSelected: false,
    ...state,
  }),

  /**
   * Wraps the filter and row-selection setters, so this must be registered after
   * `columnFilteringFeature`, `globalFilteringFeature` and `rowSelectionFeature`.
   */
  constructTableAPIs: (table) => {
    const self = apis(table);
    /**
     * Read per call, never captured: `table.options` is a fresh object every render, so a
     * snapshot taken here would pin the first render's values for the table's lifetime.
     */
    const options = () => table.options as SelectAllOptions;

    const origSetColumnFilters = self.setColumnFilters;
    const origSetGlobalFilter = self.setGlobalFilter;
    const origSetRowSelection = self.setRowSelection;

    assignTableAPIs("selectAllFeature", table, {
      table_getIsAllMatchingSelected: {
        fn: () => self.atoms.isAllMatchingSelected.get(),
      },

      table_setAllMatchingSelected: {
        fn: (value: boolean) => {
          options().onIsAllMatchingSelectedChange?.(value);
        },
      },

      table_getSelectionMode: {
        fn: (): SelectionMode => {
          if (self.atoms.isAllMatchingSelected.get()) {
            return "all";
          }
          if (self.getIsAllRowsSelected()) {
            return "page";
          }
          if (self.getIsSomeRowsSelected()) {
            return "partial";
          }
          return "none";
        },
      },

      // Wrap filter methods to auto-reset select-all-matching when filters change
      table_setColumnFilters: {
        fn: (updater: Updater<ColumnFiltersState>) => {
          origSetColumnFilters(updater);
          if (self.atoms.isAllMatchingSelected.get()) {
            self.setAllMatchingSelected(false);
          }
        },
      },

      table_setGlobalFilter: {
        fn: (updater: Updater<unknown>) => {
          origSetGlobalFilter(updater);
          if (self.atoms.isAllMatchingSelected.get()) {
            self.setAllMatchingSelected(false);
          }
        },
      },

      // Wrap setRowSelection to detect deselection and auto-exit select-all-matching
      table_setRowSelection: {
        fn: (updater: Updater<RowSelectionState>) => {
          if (options().enableSelectAllMatching && self.atoms.isAllMatchingSelected.get()) {
            const oldSelection = self.atoms.rowSelection.get();
            const newSelection = functionalUpdate(updater, oldSelection);
            const hasDeselection = Object.keys(oldSelection).some((id) => oldSelection[id] && !newSelection[id]);
            if (hasDeselection) {
              self.setAllMatchingSelected(false);
            }
          }
          origSetRowSelection(updater);
        },
      },
    });
  },
};
